import { useState, useEffect, useCallback } from 'react';
import {
  Image, Upload, Search, FolderOpen, Trash2, Edit2, Check, X,
  Grid, List, Loader2, Filter, Tag, Plus, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/utils/apiClient';

export default function MediaLibrary({ onSelect, selectionMode = true }) {
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeFolder, setActiveFolder] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [total, setTotal] = useState(0);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFolder) params.append('folder', activeFolder);
      if (search) params.append('search', search);
      params.append('limit', '50');

      const res = await api.get(`/admin/cms/media?${params}`);
      setMedia(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Media fetch error:', err);
      if (err.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load media: Network error');
      }
      setMedia([]);
      setTotal(0);
    }
    setLoading(false);
  }, [activeFolder, search]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api.get('/admin/cms/media/folders');
      const data = res.data;
      // Handle both array response and {folders: [...]} response
      setFolders(Array.isArray(data) ? data : (data.folders || ['general', 'menu', 'banners', 'logos', 'products']));
    } catch (err) {
      setFolders(['general', 'menu', 'banners', 'logos', 'products']);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
    fetchFolders();
  }, [fetchMedia, fetchFolders]);

  const handleUpload = async (files) => {
    setUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', activeFolder || 'general');

      try {
        const res = await api.post('/admin/cms/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return res.data;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);
    const successful = results.filter(r => r !== null);
    
    if (successful.length > 0) {
      toast.success(`Uploaded ${successful.length} file(s)`);
      fetchMedia();
    }
    setUploading(false);
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Delete this file?')) return;
    
    try {
      await api.delete(`/admin/cms/media/${mediaId}`);
      toast.success('File deleted');
      fetchMedia();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Delete ${selectedItems.length} files?`)) return;

    try {
      await api.post('/admin/cms/media/bulk-delete', selectedItems);
      toast.success('Files deleted');
      setSelectedItems([]);
      fetchMedia();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleUpdateMeta = async (mediaId, data) => {
    try {
      await api.put(`/admin/cms/media/${mediaId}`, data);
      toast.success('Updated');
      setEditingItem(null);
      fetchMedia();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="h-[60vh] flex flex-col" data-testid="media-library">
      {/* Toolbar */}
      <div className="flex items-center gap-3 pb-4 border-b">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Folder Filter */}
        <Select value={activeFolder || "all"} onValueChange={(val) => setActiveFolder(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[150px]">
            <FolderOpen className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All folders" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All folders</SelectItem>
            {folders.map(f => (
              <SelectItem key={f.name} value={f.name}>
                {f.name} ({f.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* View Toggle */}
        <div className="flex border rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'hover:bg-muted'}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'hover:bg-muted'}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>

        {/* Bulk Delete */}
        {selectedItems.length > 0 && (
          <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
            <Trash2 className="w-4 h-4 mr-1" />
            Delete ({selectedItems.length})
          </Button>
        )}

        {/* Upload Button */}
        <label className="cursor-pointer">
          <input
            type="file"
            multiple
            accept="image/*,video/*,.pdf"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
          <Button asChild disabled={uploading}>
            <span>
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              Upload
            </span>
          </Button>
        </label>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto pt-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : media.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No media files</p>
            <p className="text-sm">Upload images to get started</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-4 gap-4">
            {media.map((item, index) => (
              <div
                key={item.id || item._id || `media-${index}`}
                className={`relative group rounded-lg overflow-hidden border transition-all cursor-pointer ${
                  selectedItems.includes(item.id) ? 'ring-2 ring-primary' : 'hover:border-primary'
                }`}
                onClick={() => selectionMode && onSelect ? onSelect(item) : toggleSelect(item.id)}
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-muted">
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.alt || item.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-4xl">
                        {item.type === 'video' ? '🎬' : '📄'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {selectionMode && onSelect ? (
                    <Button size="sm" variant="secondary">
                      Select
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => { e.stopPropagation(); setEditingItem(item); }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>

                {/* Info */}
                <div className="p-2 bg-card">
                  <p className="text-sm font-medium truncate">{item.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatSize(item.size)}
                    {item.dimensions && ` • ${item.dimensions.width}x${item.dimensions.height}`}
                  </p>
                </div>

                {/* Selection Checkbox */}
                {!selectionMode && (
                  <div
                    className={`absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      selectedItems.includes(item.id)
                        ? 'bg-primary border-primary text-white'
                        : 'bg-white/80 border-gray-300'
                    }`}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
                  >
                    {selectedItems.includes(item.id) && <Check className="w-3 h-3" />}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {media.map((item, index) => (
              <div
                key={item.id || item._id || `media-list-${index}`}
                className={`flex items-center gap-4 p-3 rounded-lg border transition-all cursor-pointer ${
                  selectedItems.includes(item.id) ? 'ring-2 ring-primary' : 'hover:border-primary'
                }`}
                onClick={() => selectionMode && onSelect ? onSelect(item) : toggleSelect(item.id)}
              >
                {/* Thumbnail */}
                <div className="w-16 h-16 rounded bg-muted flex-shrink-0 overflow-hidden">
                  {item.type === 'image' ? (
                    <img src={item.url} alt={item.alt} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-2xl">{item.type === 'video' ? '🎬' : '📄'}</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.filename}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatSize(item.size)} • {item.folder}
                    {item.dimensions && ` • ${item.dimensions.width}x${item.dimensions.height}`}
                  </p>
                  {item.alt && <p className="text-sm text-muted-foreground truncate">Alt: {item.alt}</p>}
                </div>

                {/* Actions */}
                {!selectionMode && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); setEditingItem(item); }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Total count */}
      <div className="pt-4 border-t text-sm text-muted-foreground">
        {total} file{total !== 1 ? 's' : ''} {activeFolder && `in ${activeFolder}`}
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <MediaEditModal
          item={editingItem}
          folders={folders}
          onSave={handleUpdateMeta}
          onClose={() => setEditingItem(null)}
        />
      )}
    </div>
  );
}

function MediaEditModal({ item, folders, onSave, onClose }) {
  const [alt, setAlt] = useState(item.alt || '');
  const [tags, setTags] = useState(item.tags?.join(', ') || '');
  const [folder, setFolder] = useState(item.folder || 'general');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card rounded-lg p-6 w-[400px] max-w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Edit Media</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Preview */}
        <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-4">
          {item.type === 'image' ? (
            <img src={item.url} alt={alt} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-4xl">{item.type === 'video' ? '🎬' : '📄'}</span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <Label>Alt Text</Label>
            <Input
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Describe the image..."
            />
          </div>

          <div>
            <Label>Tags (comma-separated)</Label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="coffee, product, hero..."
            />
          </div>

          <div>
            <Label>Folder</Label>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {folders.map(f => (
                  <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button
              className="flex-1"
              onClick={() => onSave(item.id, {
                alt,
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
                folder
              })}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
